import React from "react";

import {ColumnDef} from "../../ColumnDef";
import {CURRENCY, CURRENCY_LINK, DATE, DATE_LINK, NUMBER} from "../../../constants";
import {Cell} from "../../Cell";
import {Button, Tooltip} from "@mui/material";

import {Add} from "./operations/Add.js";
import {Subtract} from "./operations/Subtract.js";
import {Multiply} from "./operations/Multiply.js";
import {Divide} from "./operations/Divide.js";
import {CheckCircle, Pending} from "@mui/icons-material";


export class Calculation extends ColumnDef {

    constructor({
        leftColumn,
        rightColumn,
        operation,
        index = null
    }) {
        super({
            displayName: `${leftColumn.displayName} ${operation.constructor.symbol} ${rightColumn.displayName}`,
            options: {...leftColumn.options, ...rightColumn.options},
            index: index, // Must be set later.
            type: Calculation.deduceType(leftColumn, rightColumn),
            style: {...leftColumn.style, ...rightColumn.style},
        });

        this.leftColumn = leftColumn; // The column on the left side of the operation
        this.rightColumn = rightColumn; // The column on the right side of the operation (if applicable)
        this.operation = operation;
    }

    static operations = {
        Add: Add,
        Subtract: Subtract,
        Multiply: Multiply,
        Divide: Divide,
    }

    static fromJSON(json) {
        const leftColumn = ColumnDef.fromJSON(json.leftColumn);
        const rightColumn = ColumnDef.fromJSON(json.rightColumn);
        const fromJson = new Calculation({
            leftColumn: leftColumn,
            rightColumn: rightColumn,
            operation: new Calculation.operations[json.operation]({leftColumn, rightColumn}),
            index: json.index,
        });
        console.log("Rehydrated Calculation from JSON:", fromJson);
        return fromJson;
    }

    toJSON() {
        const json = {
            ...super.toJSON(),
            leftColumn: this.leftColumn.toJSON(),
            rightColumn: this.rightColumn.toJSON(),
            operation: this.operation.constructor.name,
            className: this.constructor.name,
            id: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15), // Unique ID for this instance
        };
        console.log("Calculation as json:", json);
        return json;
    }

    getCell(row) {
        return new Cell(this, this.operation.perform(row), row.rowNumber);
    }

    apply(view) {
        const viewableRows = view.getViewableRows();
        for (let i = 0; i < viewableRows.length; i++) {
            this.addToRow(viewableRows[i]);
        }
    }

    static deduceType(leftColumn, rightColumn) {
        if (leftColumn.type === CURRENCY || rightColumn.type === CURRENCY) {
            return CURRENCY; // If both columns are numbers, the result is a number.
        }
        if (leftColumn.type === CURRENCY_LINK || rightColumn.type === CURRENCY_LINK) {
            return CURRENCY; // If both columns are numbers, the result is a number.
        }
        if (leftColumn.type === DATE || rightColumn.type === DATE) {
            return DATE; // If both columns are dates, the result is a date.
        }
        if (leftColumn.type === DATE_LINK || rightColumn.type === DATE_LINK) {
            return DATE; // If both columns are dates, the result is a date.
        }
        return NUMBER;
    }

    addToRow(row) {
        // This method is used to add the calculation to the row.
        if (this.getIndex() < row.cells.length && this.getIndex() !== null) {
            // If the index is less than the length of cells, we should not overwrite it.
            console.error(`Calculation index ${this.getIndex()} is less than row cells length ${row.cells.length}.`);
            throw new Error(`Calculation index ${this.getIndex()} is less than row cells length ${row.cells.length}.`);
        }
        if (this.getIndex() === null) {
            this.setIndex(row.cells.length); // Set the index to the current length of cells in the row
        }
        if (row.cells[this.getIndex()] !== undefined) {
            // If the cell already exists, we should not overwrite it.
            // This can happen if the calculation is added multiple times.
            return row;
        }
        if (row.cells.length !== this.getIndex()) {
            // If the index is not at the end of the row, we need to fill in the gaps.
            console.error(`Calculation index ${this.getIndex()} does not match row cells length ${row.cells.length}.`);
            throw new Error(`Calculation index ${this.getIndex()} does not match row cells length ${row.cells.length}.`);
        }
        row.cells.push(this.getCell(row));

        return row;
    }

    key() {
        return `calculation-${this.leftColumn.getIndex()}-${this.rightColumn.getIndex()}-${this.operation.constructor.name}`;
    }

    renderPending(view) {
        return <div
            role={"active-view-option-row"}
            className="active-view-option-row"
            key={this.key()}>
            <span
                className="active-view-option-row-label"
                role={"pending-calculation"}
            >
                <Tooltip slotProps={{
                    popper: { // Render the tooltip on top of everything else:
                        className: "canold-table-tooltip"
                    }
                }} title={"This calculation has not yet been added to the table. Click \"Update View\" to apply."}>
                    <Pending className={"canold-table-pending-icon"}/>
                </Tooltip>
                {this.leftColumn.displayName} {this.operation.constructor.symbol} {this.rightColumn.displayName}
            </span>
            <Button
                variant="outlined"
                className="pending-calculation-remove-button"
                role="pending-calculation-remove-button"
                onClick={() => view.removePendingStateChange(this)}
            >
                Remove
            </Button>
        </div>
    }

    renderApplied(view) {
        return <div
            role={"active-view-option-row"}
            className={"active-view-option-row"}
            key={this.key()}>
            <span
                className="active-view-option-row-label"
                role={"applied-calculation"}
            >
                <Tooltip slotProps={{
                    popper: { // Render the tooltip on top of everything else:
                        className: "canold-table-tooltip"
                    }
                }} title={"This calculation has been added to the table. Click Remove or Reset Table to un-apply."}>
                    <CheckCircle className={"canold-table-applied-icon"}/>
                </Tooltip>
                {this.leftColumn.displayName} {this.operation.constructor.symbol} {this.rightColumn.displayName}
            </span>
            <Button
                variant="outlined"
                className="applied-calculation-remove-button"
                role="applied-calculation-remove-button"
                onClick={() => view.removeAppliedStateChange(this)}
            >
                Remove
            </Button>
        </div>
    }
}


export default Calculation;